import { Checkbox } from "@blueprintjs/core";
import { FormikProps, getIn } from "formik";
import {
  DataTable,
  FGCheckboxInput,
  FGNumberInput,
  FGSelectInput,
  IDataTableColumn,
  useGridState,
} from "nsitools-react";
import * as React from "react";
import { css } from "styled-components";
import { CompetitionImportDto, ECompetitionResultImportStatus } from "../../../../api";
import { SmallFormGenerator } from "../../../../components";
import { useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import * as Yup from "yup";
import { useReferential } from "../../../../hooks/useReferential";
import { values } from "lodash";

type ExtendedTypeInternal = {
  item: CompetitionImportDto;
  position: number;
  isSelected: boolean;
};
type InternalFormType = {
  array: ExtendedTypeInternal[];
  shouldDouble: boolean;
  expoId: number;
};
export interface ICompetitionResultImportDataTableFormProps {
  competitionId: number;
  initialData: CompetitionImportDto[];
  onSave: (toSave: CompetitionImportDto[], shouldDouble: boolean, expoId: number) => any;
  saving: boolean;
  importFunc: () => Promise<void>;
}

export const CompetitionResultImportDataTableForm: React.FunctionComponent<
  ICompetitionResultImportDataTableFormProps
> = ({ competitionId, initialData, onSave, saving, importFunc }) => {
  const { t, tUnsafe } = useTl();
  const formikRef = React.useRef<FormikProps<InternalFormType>>();

  const [classes, classesLoading] = useReferential((a) => a.getShowClasses(), false, []);
  const [expos, exposLoading] = useReferential((a) => a.getCompetitionExpos(competitionId), true, []);
  const [judges, loadingJudges] = useReferential((a) => a.getJudges(competitionId), true, []);
  const [qualifications, loadingQualifications] = useReferential((a) => a.getQualifications(), true, []);
  const [results, loadingResults] = useReferential((a) => a.getResults(), true, []);
  const [loadingstate, setloading] = React.useState(false);
  const loading = React.useMemo(
    () => classesLoading || loadingJudges || loadingQualifications || loadingResults || loadingstate,
    [classesLoading, loadingJudges, loadingQualifications, loadingResults, loadingstate]
  );

  const finalInitialValues = React.useMemo<InternalFormType>(() => {
    let i = 0;
    return {
      shouldDouble: false,
      expoId: null,
      array: initialData.map((d) => ({
        item: d,
        position: i++,
        isSelected: false,
      })),
    };
  }, [initialData]);

  const tableState = useGridState<ExtendedTypeInternal>({
    serverMode: false,
    pageSize: 15,
    availablePageSizes: [15, 20, 50, 100],
    enablePagination: true,
  });

  const onSubmit = React.useCallback(
    async (data: InternalFormType) => {
      setloading(true);
      const toSave = data.array.filter((d) => d.item.isSelected).map((d) => d.item);

      if (toSave.length > 0) {
        onSave(toSave, data.shouldDouble, data.expoId);
      }
      await importFunc();
      setloading(false);
    },
    [importFunc, onSave]
  );

  const formSchema = React.useMemo(() => {
    return Yup.object().shape({
      expoId: Yup?.number()
        .nullable()
        .test("expoId-required", t(ETLCodes.Required), (val) => {
          if (!exposLoading && (!expos || expos.length == 0)) {
            return true;
          }
          return !!val;
        }),
      array: Yup.array().of(
        Yup.object().shape({
          item: Yup.object().shape({
            judgeId: Yup.number()
              .nullable()
              .test("is-required", t(ETLCodes.Required), async function (value) {
                if (this.parent.isSelected) {
                  return !!value;
                }
                return true;
              }),
            qualificationId: Yup.number()
              .nullable()
              .test("is-required", t(ETLCodes.Required), async function (value) {
                if (this.parent.isSelected) {
                  return value !== null;
                }
                return true;
              }),
            result: Yup.string()
              .nullable()
              .test("is-required", t(ETLCodes.Required), async function (value) {
                if (this.parent.isSelected) {
                  return value !== null;
                }
                return true;
              }),
            showClass: Yup.string()
              .nullable()
              .test("is-required", t(ETLCodes.Required), async function (value) {
                if (this.parent.isSelected) {
                  return value !== null;
                }
                return true;
              }),
          }),
        })
      ),
    });
  }, [t, expos, exposLoading]);

  const isEditable = React.useCallback((importStatus) => {
    return !(
      importStatus !== ECompetitionResultImportStatus.OK &&
      importStatus !== ECompetitionResultImportStatus.NOK_INFORMATION_MISSING &&
      importStatus !== ECompetitionResultImportStatus.NOK_JUDGE_MISSING &&
      importStatus !== ECompetitionResultImportStatus.NOK_DOG_MISSING
    );
  }, []);

  const [selectAll, setselectAll] = React.useState<boolean>();

  const toggleSelectAll = React.useCallback(
    (checked: boolean) => {
      var page = tableState.data;
      console.log("checked", checked);
      if (checked) {
        console.log("page", page);
        console.log("value", formikRef.current.values.array[1].item.judgeId);
        page?.map((v, i) => {
          if (
            isEditable(v.item.importStatus) &&
            formikRef.current.values.array[i].item.judgeId > 0 &&
            formikRef.current.values.array[i].item.importStatus === ECompetitionResultImportStatus.OK
          ) {
            formikRef.current.setFieldValue(`array[${i}].item.isSelected`, true);
          } else {
            formikRef.current.setFieldValue(`array[${i}].item.isSelected`, false);
          }
        });
      } else {
        formikRef.current.values.array.map((v, i) =>
          formikRef.current.setFieldValue(`array[${i}].item.isSelected`, false)
        );
      }
    },
    [isEditable, tableState.data]
  );

  const columns = React.useMemo<IDataTableColumn<ExtendedTypeInternal>[]>(
    () => [
      {
        computed: true,
        disableSorting: true,
        fieldName: "isSelected",
        header: () => (
          <Checkbox
            checked={selectAll}
            onClick={(e) => {
              setselectAll(e.currentTarget.checked);
              toggleSelectAll(e.currentTarget.checked);
            }}
          />
        ),
        render: (item) => {
          return (
            <FGCheckboxInput
              name={`array.${item.position}.item.isSelected`}
              disabled={!isEditable(item.item.importStatus)}
              noLabel
            />
          );
        },
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "status",
        header: () => t(ETLCodes.Status),
        render: (item) => (
          <div
            style={item.item.importStatus === ECompetitionResultImportStatus.OK ? { color: "green" } : { color: "red" }}
          >
            {tUnsafe(item.item.importStatus)}
          </div>
        ),
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "automaticCreation",
        autoFitContent: true,
        alignement: "center",
        header: () => t(ETLCodes.AutomaticCreation),
        render: (item) => <Checkbox checked={item.item.automaticDogCreation} disabled />,
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "dogName",
        header: () => t(ETLCodes.Dog),
        render: (item) => <div>{item.item.automaticDogDto.firstName}</div>,
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "judgeId",
        header: () => t(ETLCodes.Judge),
        render: (item) => (
          <div style={{ minWidth: "10rem" }}>
            <FGSelectInput name={`array.${item.position}.item.judgeId`} items={judges} noLabel isCleareable={false} />
          </div>
        ),
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "qualificationId",
        header: () => t(ETLCodes.Qualification),
        render: (item) => (
          <div style={{ minWidth: "10rem" }}>
            <FGSelectInput
              name={`array.${item.position}.item.qualificationId`}
              items={qualifications}
              isCleareable={false}
              noLabel
            />
          </div>
        ),
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "catalogNumber",
        header: () => t(ETLCodes.CatalogNumber),
        render: (item) => (
          <div style={{ minWidth: "2rem" }}>
            <FGNumberInput name={`array.${item.position}.item.catalogNumber`} />
          </div>
        ),
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "result",
        header: () => t(ETLCodes.Result),
        render: (item) => (
          <div style={{ minWidth: "5rem" }}>
            <FGSelectInput name={`array.${item.position}.item.result`} items={results} noLabel isCleareable={false} />
          </div>
        ),
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "class",
        header: () => t(ETLCodes.Class),
        render: (item) => (
          <div style={{ minWidth: "5rem" }}>
            <FGSelectInput
              name={`array.${item.position}.item.showClass`}
              items={classes}
              noLabel
              isCleareable={false}
            />
          </div>
        ),
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "standardConformed",
        alignement: "center",
        header: () => t(ETLCodes.BreedConformity),
        render: (item) => <Checkbox checked={item.item.standardConformed} disabled />,
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "bestOfBreed",
        alignement: "center",
        header: () => t(ETLCodes.BestOfBreed),
        render: (item) => <Checkbox checked={item.item.bestOfBreed} disabled />,
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "bestOfSex",
        alignement: "center",
        header: () => t(ETLCodes.BestOfSex),
        render: (item) => <Checkbox checked={item.item.bestOfSex} disabled />,
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "bestInShow",
        alignement: "center",
        header: () => t(ETLCodes.BestInShow),
        render: (item) => <Checkbox checked={item.item.bestInShow} disabled />,
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "bestInGroup",
        alignement: "center",
        header: () => t(ETLCodes.BestInGroup),
        render: (item) => <Checkbox checked={item.item.bestInGroup} disabled />,
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "bestVeteran",
        alignement: "center",
        header: () => t(ETLCodes.BestVeteran),
        render: (item) => <Checkbox checked={item.item.bestVeteran} disabled />,
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "bestWorkDog",
        alignement: "center",
        header: () => t(ETLCodes.BestWorkDog),
        render: (item) => <Checkbox checked={item.item.bestWorkDog} disabled />,
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "bestYouth",
        alignement: "center",
        header: () => t(ETLCodes.BestYouth),
        render: (item) => <Checkbox checked={item.item.bestYouth} disabled />,
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "bestMinorPuppy",
        alignement: "center",
        header: () => t(ETLCodes.BestMinorPuppy),
        render: (item) => <Checkbox checked={item.item.bestMinorPuppy} disabled />,
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "bestPuppy",
        alignement: "center",
        header: () => t(ETLCodes.BestPuppy),
        render: (item) => <Checkbox checked={item.item.bestPuppy} disabled />,
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "belgianWinner",
        alignement: "center",
        header: () => t(ETLCodes.BelgianWinner),
        render: (item) => <Checkbox checked={item.item.belgianWinner} disabled />,
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "beneluxWinner",
        alignement: "center",
        header: () => t(ETLCodes.BeneluxWinner),
        render: (item) => <Checkbox checked={item.item.beneluxWinner} disabled />,
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "beneluxYouthWinner",
        alignement: "center",
        header: () => t(ETLCodes.BeneluxYouthWinner),
        render: (item) => <Checkbox checked={item.item.beneluxYouthWinner} disabled />,
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "beneluxVeteranWinner",
        alignement: "center",
        header: () => t(ETLCodes.BeneluxVeteranWinner),
        render: (item) => <Checkbox checked={item.item.beneluxVeteranWinner} disabled />,
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "europeanWinner",
        alignement: "center",
        header: () => t(ETLCodes.EuropeanWinner),
        render: (item) => <Checkbox checked={item.item.europeanWinner} disabled />,
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "crufts",
        alignement: "center",
        header: () => t(ETLCodes.Crufts),
        render: (item) => <Checkbox checked={item.item.crufts} disabled />,
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "cac",
        alignement: "center",
        header: () => t(ETLCodes.Cac),
        render: (item) => <Checkbox checked={item.item.cac} disabled />,
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "cacReserve",
        alignement: "center",
        header: () => t(ETLCodes.CacReserve),
        render: (item) => <Checkbox checked={item.item.cacReserve} disabled />,
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "cacib",
        alignement: "center",
        header: () => t(ETLCodes.Cacib),
        render: (item) => <Checkbox checked={item.item.cacib} disabled />,
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "cacibReserve",
        alignement: "center",
        header: () => t(ETLCodes.CacibReserve),
        render: (item) => <Checkbox checked={item.item.cacibReserve} disabled />,
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "cacbl",
        alignement: "center",
        header: () => t(ETLCodes.Cacbl),
        render: (item) => <Checkbox checked={item.item.cacbl} disabled />,
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "cacblReserve",
        alignement: "center",
        header: () => t(ETLCodes.CacblReserve),
        render: (item) => <Checkbox checked={item.item.cacblReserve} disabled />,
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "cacgpo",
        alignement: "center",
        header: () => t(ETLCodes.Cacgpo),
        render: (item) => <Checkbox checked={item.item.cacgpo} disabled />,
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "cacgpoReserve",
        alignement: "center",
        header: () => t(ETLCodes.CacgpoReserve),
        render: (item) => <Checkbox checked={item.item.cacgpoReserve} disabled />,
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "caciag",
        alignement: "center",
        header: () => t(ETLCodes.Caciag),
        render: (item) => <Checkbox checked={item.item.caciag} disabled />,
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "caciagReserve",
        alignement: "center",
        header: () => t(ETLCodes.CaciagReserve),
        render: (item) => <Checkbox checked={item.item.caciagReserve} disabled />,
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "cacil",
        alignement: "center",
        header: () => t(ETLCodes.Cacil),
        render: (item) => <Checkbox checked={item.item.cacil} disabled />,
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "cacilReserve",
        alignement: "center",
        header: () => t(ETLCodes.CacilReserve),
        render: (item) => <Checkbox checked={item.item.cacilReserve} disabled />,
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "caciob",
        alignement: "center",
        header: () => t(ETLCodes.Caciob),
        render: (item) => <Checkbox checked={item.item.caciob} disabled />,
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "caciobReserve",
        alignement: "center",
        header: () => t(ETLCodes.CaciobReserve),
        render: (item) => <Checkbox checked={item.item.caciobReserve} disabled />,
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "cacit",
        alignement: "center",
        header: () => t(ETLCodes.Cacit),
        render: (item) => <Checkbox checked={item.item.cacit} disabled />,
      },
    ],
    [classes, isEditable, judges, qualifications, results, selectAll, t, tUnsafe, toggleSelectAll]
  );

  const { setData } = tableState;

  React.useEffect(() => {
    setData([...finalInitialValues.array]);
  }, [finalInitialValues, setData]);

  return (
    <SmallFormGenerator
      initialValues={finalInitialValues}
      onSubmit={onSubmit}
      saving={saving}
      loading={loading}
      enableDirtyCheck={false}
      editMode
      formikInnerRef={(f) => (formikRef.current = f)}
      validationSchema={formSchema}
    >
      <DataTable
        tableState={tableState}
        columns={columns}
        loading={saving}
        customizeTableStyle={() => css`
          border-collapse: collapse;
          width: 100%;
          overflow: scroll;
          display: block !important;
          max-height: 600px;
          border: solid 1px rgba(16, 22, 26, 0.15);
        `}
      ></DataTable>
      <FGCheckboxInput name="shouldDouble" label={t(ETLCodes.ShouldDoubleResults)} />
      {expos?.length > 0 && (
        <FGSelectInput
          name={`expoId`}
          label={t(ETLCodes.Expo)}
          items={expos}
          loading={exposLoading}
          requiredMark={expos?.length > 0}
        />
      )}
    </SmallFormGenerator>
  );
};
